"use client";

import { Skeleton } from "@cantera/design-system";
import { AuthView } from "@neondatabase/auth-ui";
import { authViewPaths } from "@neondatabase/auth-ui/server";
import type { ReactNode } from "react";
import { useHydrated } from "@/hooks/use-hydrated";

const CARD_CLASSNAME =
  "w-full max-w-sm bg-card border-0 shadow-none lg:shadow-lg lg:rounded-lg dark:lg:border dark:lg:border-border";

/**
 * Renders the auth form for a view path.
 */
export function AuthViewForm({
  path,
  cardHeader,
  hideFieldLabel,
}: {
  path: string;
  cardHeader?: ReactNode;
  hideFieldLabel?: boolean;
}) {
  const hydrated = useHydrated();

  /* AuthView reads ?email= from window as it renders. If the page is reloaded, 
  we render a loading state until after hydration so that the email is available,
  otherwise reloading the page will produce an error */
  if (path === authViewPaths.EMAIL_VERIFICATION && !hydrated) {
    return (
      <div aria-busy="true" className={CARD_CLASSNAME}>
        <div className="px-6 pt-6">{cardHeader}</div>
        <div className="flex flex-col gap-3 p-6">
          <Skeleton className="h-10" />
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10 w-3/4" />
        </div>
      </div>
    );
  }

  return (
    <AuthView
      path={path}
      classNames={{
        base: CARD_CLASSNAME,
        content: "flex flex-col-reverse",
        form: {
          // If label should be hidden, it's still kept for screen readers
          label: hideFieldLabel ? "sr-only" : undefined,
          providerButton: "hover:bg-accent active:bg-accent",
          input: "text-foreground shadow-none",
          // OTP slots are styled in globals.css
          otpInputContainer: "auth-otp",
        },
      }}
      cardHeader={cardHeader}
    />
  );
}
